import { useEffect, useState } from 'react';
import api from '../../api';

function CarouselManager() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [formData, setFormData] = useState({ caption: '', display_order: 0 });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      const res = await api.get('/api/admin/carousel');
      setSlides(res.data);
    } catch (err) {
      console.error('Failed to fetch carousel:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (slide = null) => {
    if (slide) {
      setEditingSlide(slide);
      setFormData({ caption: slide.caption || '', display_order: slide.display_order ?? 0 });
      setImageFile(null);
    } else {
      setEditingSlide(null);
      setFormData({ caption: '', display_order: slides.length });
      setImageFile(null);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSlide(null);
    setImageFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingSlide && !imageFile) {
      alert('Please select an image.');
      return;
    }
    if (editingSlide && !imageFile && !editingSlide.image_url) {
      alert('Please select an image.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('caption', formData.caption);
      fd.append('display_order', String(formData.display_order));
      if (imageFile) fd.append('image', imageFile);

      // Do not set Content-Type so axios/browser sets multipart/form-data with boundary
      const config = { headers: { 'Content-Type': null } };
      if (editingSlide) {
        await api.put(`/api/admin/carousel/${editingSlide.id}`, fd, config);
      } else {
        await api.post('/api/admin/carousel', fd, config);
      }
      closeModal();
      fetchSlides();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save slide');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this slide from the home carousel?')) return;
    try {
      await api.delete(`/api/admin/carousel/${id}`);
      fetchSlides();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  if (loading) {
    return <div className="loading">Loading carousel...</div>;
  }

  return (
    <div>
      <div className="admin-header">
        <h1>Home Carousel</h1>
        <button onClick={() => openModal()} className="btn btn-primary">+ Add Slide</button>
      </div>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
        Photos shown on the home page below the site name. Order by display order. Add as many slides as you like (e.g. 3 or more).
      </p>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Preview</th>
              <th>Caption</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {slides.map((slide) => (
              <tr key={slide.id}>
                <td>{slide.display_order}</td>
                <td>
                  <img src={slide.image_url} alt="" style={{ width: '80px', height: '48px', objectFit: 'cover', borderRadius: '4px' }} />
                </td>
                <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {slide.caption || '—'}
                </td>
                <td>
                  <button onClick={() => openModal(slide)} className="btn btn-small btn-secondary" style={{ marginRight: '5px' }}>Edit</button>
                  <button onClick={() => handleDelete(slide.id)} className="btn btn-small btn-danger">Delete</button>
                </td>
              </tr>
            ))}
            {slides.length === 0 && (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                  No slides yet. Add at least one to show the carousel on the home page.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" style={{ maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
            <h2>{editingSlide ? 'Edit Slide' : 'Add Slide'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Image *</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => setImageFile(e.target.files?.[0] || null)}
                  required={!editingSlide}
                />
                {editingSlide?.image_url && !imageFile && (
                  <div style={{ marginTop: '8px' }}>
                    <img src={editingSlide.image_url} alt="" style={{ maxWidth: '200px', maxHeight: '120px', objectFit: 'cover', borderRadius: '4px' }} />
                    <span style={{ display: 'block', marginTop: '4px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Upload a new image to replace</span>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Caption (optional)</label>
                <input
                  type="text"
                  value={formData.caption}
                  onChange={e => setFormData({ ...formData, caption: e.target.value })}
                  placeholder="Short caption on the slide"
                />
              </div>
              <div className="form-group">
                <label>Display order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={e => setFormData({ ...formData, display_order: parseInt(e.target.value, 10) || 0 })}
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={closeModal} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={uploading}>
                  {uploading ? 'Saving...' : (editingSlide ? 'Save' : 'Add')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CarouselManager;
